import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import {
  formatPostCode,
  notificationWeeksValidationRule,
  taxCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  getAgencyRefMethodOptions,
  defaultCandidateStatus,
  defaultMarginMethodCompany,
  defaultNonEUVerifiedIdMethodOptions,
  defaultNonUKVerifiedIdMethodOptions,
  defaultOnBoardingOptions,
  defaultPaymentFrequency,
  getPaymentMethodOptions,
  defaultPaymentTerm,
  defaultPRSIEeClassesOptions,
  defaultTaxBasisIrelandOptions,
  getTimeSheetImportValidationOptions,
  defaultUKVerifiedIdMethodOptions,
  NICategoryOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { countryOptions } from '@nxsys/constants/countries';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle, useMemo } from 'react';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';
import { enumMarginMethod } from '@nxsys/constants/AppEnums';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

export type CompanyDefaultValues = {
  paymentTerm: Option<string> | null;
  NICategory: Option<string> | null;
  taxCode: string;
  paymentMethod: Option<string> | null;
  paymentFrequency: Option<string> | null;
  passProtectDoc: boolean | null;
  candidateStatus: Option<string> | null;
  agencyRefMethod: Option<string> | null;
  marginMethod: Option<string> | null;
  marginCapped: string;
  margin: string;
  minMargin: string;
  timesheetImportValidation: Option<string> | null;
  isRequiredAttachmentExpenses: boolean | null;
  isRestrictedHours: boolean | null;
  invoicePrefix: string;
  invoiceNextNumber: string;
  creditNotePrefix: string;
  creditNoteNextNumber: string;
  subOnboarding: boolean | null;
  onboarding: Option<string>[];
  UKVerifiedIdMethod: Option<string> | null;
  nonUKVerifiedIdMethod: Option<string> | null;
  emailOnboardingNotification: string;
  enableAWRTracking: boolean | null;
  notificationWeeks: string | number[] | null;
  enableWTR: boolean | null;
  invoicingDetailsSameAsCompanyDetails: boolean | null;
  enableBenefits: boolean | null;
  // Ireland
  taxBasis: Option<string> | null;
  prsiEeClasses: Option<string> | null;

  companyLogo: File | string | null;
  invoicingCompanyName: string;
  invoicingCompanyNo: string;
  invoicingTradingName: string;
  invoicingAddress1: string;
  invoicingAddress2: string;
  invoicingAddress3: string;
  invoicingAddress4: string;
  invoicingPostCode: string;
  invoicingCity: string;
  invoicingCountry: Option<string> | null;
  invoicingCounty: string;
  invoicingVATRegNo: string;
};

export type CompanyDefaultFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => CompanyDefaultValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<CompanyDefaultFormRef>;
  initialValues: Partial<CompanyDefaultValues>;
  isIreland?: boolean;
  stepNumber: number;
};

const CompanyDefault: React.FC<Props> = ({
  formRef,
  initialValues,
  isIreland,
  stepNumber,
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const [form] = Form.useForm<CompanyDefaultValues>();
  const watchValues = Form.useWatch([], form);
  // Margin fields are GBP when Fixed Fee, % otherwise — pick the input
  // purpose by mode (suffix below follows the same switch).
  const MarginValueInput =
    watchValues?.marginMethod?.value === 'FIXED_FEE'
      ? AppCurrencyInput
      : AppRateInput;

  const onboardingOptions = useMemo(() => {
    return defaultOnBoardingOptions
      .filter((item) => {
        if (item.isBureau === false && isBureau) return false;
        if (item.isUk && isIreland) return false;
        if (item.isIreland && !isIreland) return false;
        return true;
      })
      .map((item) =>
        isIreland && item.value === 'ADD_NI_NUMBER'
          ? { ...item, label: 'Add PPS Number' }
          : item,
      );
  }, [isBureau, isIreland]);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="CompanyDefault">
      <StepTitle
        step={stepNumber}
        title={isBureau ? 'Employer Defaults' : 'Company Defaults'}
        description={
          isBureau
            ? 'Set up your employer defaults'
            : 'Set up your company defaults'
        }
      />
      <div className="mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            paymentTerm: {
              label: 'Full Cash Receipt',
              value: 'FULL_CASH_RECEIPT',
            },
            timesheetImportValidation: {
              label: 'Agency Reference + Forename & Surname',
              value: 'AGENCY_REFERENCE_FORENAME_SURNAME',
            },
            agencyRefMethod: {
              label: isIreland ? 'PPS Number' : 'NI Number',
              value: 'NI_NUMBER',
            },
            taxCode: '1257L',
            NICategory: { label: 'A', value: 'A' },
            // Ireland
            taxBasis: 'W1M1',
            prsiEeClasses: 'A0_A1',
            paymentFrequency: isBureau
              ? { label: 'Monthly', value: 'MONTHLY' }
              : { label: 'Weekly', value: 'WEEKLY' },
            paymentMethod: { label: 'Faster', value: 'FASTER' },
            candidateStatus: {
              label: 'Registration Complete',
              value: 'REGISTRATION_COMPLETE',
            },

            passProtectDoc: true,
            margin: '',
            marginCapped: '',
            marginMethod: { label: 'Fixed Fee', value: 'FIXED_FEE' },
            minMargin: '',
            isRestrictedHours: false,
            isRequiredAttachmentExpenses: false,
            // onboarding
            subOnboarding: false,
            onboarding: [],
            emailOnboardingNotification: null,
            UKVerifiedIdMethod: {
              label: 'Accept Birth Certificate',
              value: 'ACCEPT_BIRTH_CERTIFICATE',
            },
            nonUKVerifiedIdMethod: isIreland
              ? defaultNonEUVerifiedIdMethodOptions[0]
              : defaultNonUKVerifiedIdMethodOptions[0],
            // invocice/credit note
            invoicePrefix: 'INV-',
            invoiceNextNumber: '0001',
            creditNotePrefix: 'CRN-',
            creditNoteNextNumber: '0001',
            enableAWRTracking: true,
            notificationWeeks: '',
            enableWTR: true,
            invoicingDetailsSameAsCompanyDetails: true,
            enableBenefits: false,
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {/* General Information Section */}
          <div className="mb-[24px]">
            <div className="flex items-center gap-x-[8px] mb-[16px]">
              <IconWrapper icon="IconLayerGroup" />
              <div className="text-[16px] font-semibold">
                General Information
              </div>
            </div>
            <div className="space-y-[16px]">
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Payment Term"
                    name="paymentTerm"
                    // rules={[
                    // {
                    // required: true,
                    // message:
                    // messages['form.pleaseSelect'] ||
                    // 'Please select payment term',
                    // },
                    // ]}
                  >
                    <AppSelect
                      placeholder="Select payment term"
                      options={defaultPaymentTerm}
                    />
                  </Form.Item>
                </Col>
                {!isIreland && (
                  <>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="Tax Code"
                        name="taxCode"
                        rules={[taxCodeValidationRule()]}
                      >
                        <AppInput upperCaseAll placeholder="Enter tax code" />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="NI Category"
                        name="NICategory"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select NI category',
                        // },
                        // ]}
                      >
                        <AppSelect
                          placeholder="Enter NI category"
                          options={NICategoryOptions}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
                {isIreland && (
                  <>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="Tax Basis"
                        name="taxBasis"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select tax basis',
                        // },
                        // ]}
                      >
                        <AppSelect
                          placeholder="Select tax basis"
                          options={defaultTaxBasisIrelandOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="PRSI EE Classes"
                        name="prsiEeClasses"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select PRSI EE class',
                        // },
                        // ]}
                      >
                        <AppSelect
                          placeholder="Select PRSI EE class"
                          options={defaultPRSIEeClassesOptions}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
                <Col sm={24} md={12}>
                  <Form.Item
                    label="Payment Frequency"
                    name="paymentFrequency"
                    // rules={[
                    // {
                    // required: true,
                    // message:
                    // messages['form.pleaseSelect'] ||
                    // 'Please select payment frequency',
                    // },
                    // ]}
                  >
                    <AppSelect
                      placeholder="Select payment frequency"
                      options={defaultPaymentFrequency}
                    />
                  </Form.Item>
                </Col>
                {!isIreland && <Col sm={24} md={12}>
                  <Form.Item
                    label="Payment Method"
                    name="paymentMethod"
                    // rules={[
                    // {
                    // required: true,
                    // message:
                    // messages['form.pleaseSelect'] ||
                    // 'Please select payment method',
                    // },
                    // ]}
                  >
                    <AppSelect
                      placeholder="Select payment method"
                      options={getPaymentMethodOptions(!!isIreland)}
                    />
                  </Form.Item>
                </Col>}
                <Col sm={24} md={12}>
                  <Form.Item
                    label="Candidate Status"
                    name="candidateStatus"
                    // rules={[
                    // {
                    // required: true,
                    // message:
                    // messages['form.pleaseSelect'] ||
                    // 'Please select candidate status',
                    // },
                    // ]}
                  >
                    <AppSelect
                      placeholder="Select candidate status"
                      options={defaultCandidateStatus}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label="Password Protect Documents?"
                    name="passProtectDoc"
                    // rules={[
                    // {
                    // required: true,
                    // message:
                    // messages['form.pleaseSelect'] ||
                    // 'Please select an option',
                    // },
                    // ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={
                      isBureau
                        ? (messages['common.employerReference'] ??
                          'Employer Reference')
                        : (messages['common.agencyReference'] ??
                          'Agency Reference')
                    }
                    name="agencyRefMethod"
                  >
                    <AppSelect
                      placeholder={messages['form.select']}
                      options={getAgencyRefMethodOptions(!!isIreland)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>

          {/* Payment Information Section — Umbrella only */}
          {!isBureau && (
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconMoneyBill" />
                <div className="text-[16px] font-semibold">
                  Payment Information
                </div>
              </div>
              <div className="space-y-[16px]">
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      label="Margin Method"
                      name="marginMethod"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseSelect'] ||
                      // 'Please select margin method',
                      // },
                      // ]}
                    >
                      <AppSelect
                        placeholder="Select margin method"
                        options={defaultMarginMethodCompany}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Margin" name="margin">
                      <MarginValueInput
                        placeholder="Enter margin"
                        suffix={
                          watchValues?.marginMethod?.value
                            ? watchValues?.marginMethod?.value === 'FIXED_FEE'
                              ? showCurrencyByCountry(payrollCountry)
                              : '%'
                            : undefined
                        }
                      />
                    </Form.Item>
                  </Col>
                  {watchValues?.marginMethod?.value ===
                    enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE && (
                    <Col sm={24} md={12}>
                      <Form.Item label="Minimum Margin" name="minMargin">
                        <MarginValueInput
                          placeholder="Enter minimum margin"
                          suffix={
                            watchValues?.marginMethod?.value
                              ? watchValues?.marginMethod?.value === 'FIXED_FEE'
                                ? showCurrencyByCountry(payrollCountry)
                                : '%'
                              : undefined
                          }
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col sm={24} md={12}>
                    <Form.Item label="Margin Capped" name="marginCapped">
                      <MarginValueInput
                        placeholder="Enter margin capped"
                        suffix={
                          watchValues?.marginMethod?.value
                            ? watchValues?.marginMethod?.value === 'FIXED_FEE'
                              ? showCurrencyByCountry(payrollCountry)
                              : '%'
                            : undefined
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Timesheet Information Section — Umbrella only */}
          {!isBureau && (
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconDocumentList" />
                <div className="text-[16px] font-semibold">
                  Timesheet Information
                </div>
              </div>
              <div className="space-y-[16px]">
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      label="Timesheet Import Validation"
                      name="timesheetImportValidation"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseSelect'] ||
                      // 'Please select timesheet import validation',
                      // },
                      // ]}
                    >
                      <AppSelect
                        placeholder="Select timesheet import validation"
                        options={getTimeSheetImportValidationOptions(isBureau)}
                      />
                    </Form.Item>
                  </Col>
                  {/* NX-5228: The 20-hour restricted-hours check only runs for UK payrolls, and only for a
                      Tier 4 student visa (getIsRestrictedHoursValidation). Ireland's Stamp 2 rule
                      differs (20 hours in term, 40 in holidays), so the UK check could not stand in
                      for it either. */}
                  {!isIreland && (
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="Restricted Hours?"
                        name="isRestrictedHours"
                        tooltip={messages['common.restrictedHoursTooltip']}
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select an option',
                        // },
                        // ]}
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                    </Col>
                  )}
                  {!isIreland && (
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="Expense Receipt Mandatory?"
                        name="isRequiredAttachmentExpenses"
                        tooltip="Workers must attach a receipt before claiming an expense."
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select an option',
                        // },
                        // ]}
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          )}

          {/* Invoice Information Section — Umbrella only */}
          {!isBureau && (
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconInvoice" />
                <div className="text-[16px] font-semibold">
                  Invoice Information
                </div>
              </div>
              <div className="space-y-[16px]">
                <Row gutter={16}>
                  <Col sm={24} md={12}>
                    <Form.Item label="INV Prefix" name="invoicePrefix">
                      <AppInput placeholder="Enter INV prefix" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Next INV Number" name="invoiceNextNumber">
                      <AppInput placeholder="Enter next INV number" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="Credit Note Prefix"
                      name="creditNotePrefix"
                    >
                      <AppInput placeholder="Enter credit note prefix" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="Next Credit Note Number"
                      name="creditNoteNextNumber"
                    >
                      <AppInput placeholder="Enter next credit note number" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Other Information Section */}
          <div className="mb-[24px]">
            <div className="flex items-center gap-x-[8px] mb-[16px]">
              <IconWrapper icon="IconGridSquarePlus" />
              <div className="text-[16px] font-semibold">Other Information</div>
            </div>
            <div className="space-y-[16px]">
              <Row gutter={[16, 16]}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={
                      messages['common.employeeAndSubcontractorOnboarding']
                    }
                    name="subOnboarding"
                    // rules={[
                    // {
                    // required: true,
                    // message:
                    // messages['form.pleaseSelect'] ||
                    // 'Please select an option',
                    // },
                    // ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.subOnboarding && (
                    <>
                      <Form.Item
                        label="Onboarding"
                        name="onboarding"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select onboarding type',
                        // },
                        // ]}
                      >
                        <AppSelect
                          placeholder="Select onboarding type"
                          options={onboardingOptions}
                          mode="multiple"
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          isIreland
                            ? 'EU Nationals - No Passport'
                            : 'UK Nationals - No Passport'
                        }
                        name="UKVerifiedIdMethod"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select',
                        // },
                        // ]}
                      >
                        <AppSelect
                          placeholder="Select"
                          options={defaultUKVerifiedIdMethodOptions}
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          isIreland
                            ? 'Non EU Nationals - No Passport'
                            : 'Non UK Nationals - No Passport'
                        }
                        name="nonUKVerifiedIdMethod"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseSelect'] ||
                        // 'Please select',
                        // },
                        // ]}
                      >
                        <AppSelect
                          placeholder="Select"
                          options={
                            isIreland
                              ? defaultNonEUVerifiedIdMethodOptions
                              : defaultNonUKVerifiedIdMethodOptions
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label="Onboarding Email Notifications"
                        name="emailOnboardingNotification"
                        // rules={[
                        // {
                        // required: true,
                        // message:
                        // messages['form.pleaseEnter'] || 'Please enter',
                        // },
                        // ]}
                      >
                        <AppInput placeholder="Enter" allowCapitalize={false} />
                      </Form.Item>
                    </>
                  )}
                </Col>

                {!isBureau && (
                  <>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="Enable AWR Tracking"
                        name="enableAWRTracking"
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                      {watchValues?.enableAWRTracking && (
                        <Form.Item
                          label="Notification Weeks"
                          name="notificationWeeks"
                          rules={[notificationWeeksValidationRule(messages)]}
                        >
                          <AppInput
                            placeholder={
                              messages['common.notificationWeeksPlaceholder']
                            }
                          />
                        </Form.Item>
                      )}
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label="Enable Working Time Regulation Tracking"
                        name="enableWTR"
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                    </Col>
                  </>
                )}
                {!isBureau && (
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="Invoicing Details Same as Company Details?"
                      name="invoicingDetailsSameAsCompanyDetails"
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                  </Col>
                )}
                {!isBureau && (
                  <Col sm={24} md={12}>
                    <Form.Item label="Enable Benefits" name="enableBenefits">
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </div>
          </div>
          {watchValues?.invoicingDetailsSameAsCompanyDetails === false && (
            <div className="mb-[24px]">
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconInvoice" />
                <div className="text-[16px] font-semibold">
                  Invoicing Details
                </div>
              </div>
              <div className="space-y-[16px]">
                <Row gutter={16}>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={isBureau ? 'Employer Name' : 'Company Name'}
                      name="invoicingCompanyName"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseEnter'] ||
                      // 'Please enter company name',
                      // },
                      // ]}
                    >
                      <AppInput
                        placeholder={
                          isBureau
                            ? 'Enter employer name'
                            : 'Enter company name'
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="Trading Name"
                      name="invoicingTradingName"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseEnter'] ||
                      // 'Please enter trading name',
                      // },
                      // ]}
                    >
                      <AppInput placeholder="Enter trading name" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="Address1"
                      name="invoicingAddress1"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseEnter'] ||
                      // 'Please enter address',
                      // },
                      // ]}
                    >
                      <AppInput placeholder="Enter address 1" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="Address2"
                      name="invoicingAddress2"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseEnter'] ||
                      // 'Please enter address',
                      // },
                      // ]}
                    >
                      <AppInput placeholder="Enter address 2" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Address3" name="invoicingAddress3">
                      <AppInput placeholder="Enter address 3" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Address4" name="invoicingAddress4">
                      <AppInput placeholder="Enter address 4" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={isIreland ? 'Eircode' : 'Post Code'}
                      name="invoicingPostCode"
                      // rules={[
                      // {
                      // required: true,
                      // message:
                      // messages['form.pleaseEnter'] ||
                      // 'Please enter post code',
                      // },
                      // ]}
                    >
                      <AppInput
                        placeholder={
                          isIreland ? 'Enter Eircode' : 'Enter post code'
                        }
                        upperCaseAll
                        onChange={(e) => {
                          form.setFieldValue(
                            'invoicingPostCode',
                            formatPostCode(e.target.value, payrollCountry),
                          );
                        }}
                        maxLength={8}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="City/Town" name="invoicingCity">
                      <AppInput placeholder="Enter city/town" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="County" name="invoicingCounty">
                      <AppInput placeholder="Enter county" />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item label="Country" name="invoicingCountry">
                      <AppSelect
                        placeholder="Select country"
                        options={countryOptions}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={isBureau ? 'Employer Number' : 'Company Number'}
                      name="invoicingCompanyNo"
                    >
                      <AppInput
                        placeholder={
                          isBureau
                            ? 'Enter employer number'
                            : 'Enter company number'
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label="VAT Reg. Number"
                      name="invoicingVATRegNo"
                      rules={[
                        {
                          pattern: isIreland ? /^IE\d{9}$/ : /^\d{9}$/,
                          message: 'VAT number must be exactly 9 digits',
                        },
                      ]}
                      {...(isIreland && {
                        normalize: (value: string) =>
                          value?.startsWith('IE') ? value : 'IE',
                        initialValue: 'IE',
                      })}
                    >
                      <AppInput
                        placeholder="Enter VAT reg. number"
                        maxLength={isIreland ? 11 : 9}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      name="companyLogo"
                      label={isBureau ? 'Employer Logo' : 'Company Logo'}
                    >
                      <AppUploadFile
                        urlFile={
                          typeof watchValues?.companyLogo === 'string'
                            ? watchValues?.companyLogo
                            : undefined
                        }
                        onChange={(file: File) => {
                          form.setFieldsValue({ companyLogo: file });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </AppFormContainer>
      </div>
    </div>
  );
};

export default CompanyDefault;
